import React from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import MapMarkerIcon from '@deseretbook/icons/react/map-marker'
import TagsIcon from '@deseretbook/icons/react/tags'
import EnvelopeIcon from '@deseretbook/icons/react/envelope'
import ShoppingCartIcon from '@deseretbook/icons/react/shopping-cart'
import UserProfileIcon from '@deseretbook/icons/react/user-profile'
import CogIcon from '@deseretbook/icons/react/cog'
import { prismicHelpers, urlHelpers } from '../../../helpers'
import { ChevronIcon } from '../../core/icons'
import PromoBanners from '../../promoBanners/PromoBanners'

/**
 * Mobile Menu Page Component.
 *
 * Renders a single page of content for the Mobile Menu. Also renders children
 * pages.
 *
 * @param {string} previousTitle - the previous title
 * @param {function} toggleSidebar - toggles the sidebar
 * @param {object} menuState - Contains the current sidebar state and levels
 * @param {function} changeLevel - changes the current sidebar content level
 * @param {integer} level - the current sidebar content level
 * @param {object} content - Content item information
 * @param {node} children - children to be rendered
 *
 * @returns {function} React component
 */
const MobileMenuPage = ({
  previousTitle,
  toggleSidebar,
  menuState,
  changeLevel,
  level,
  content,
  children,
}) => {
  /**
   * Handles link click event. Wraps link in an event tracker to track usage of
   * mobile menu.
   *
   * If the link happens to have the 'openChat' context, the chat bubble is opened
   * instead of following the link location.
   * @param e {object} click event.
   * @param link {obj} link
   * @param href {string} href to set location on completion.
   */
  const onLinkClick = (e, link, href) => {
    e.preventDefault()

    try {
      const context = JSON.parse(link.context)

      if (context.action === 'openChat') {
        if (window.CustomerSupport && typeof window.CustomerSupport.openChat === 'function') {
          window.CustomerSupport.openChat()
          toggleSidebar('Contact Us Link')
        }

        return false
      }
    } catch (error) {
      // Doesn't matter
    }

    $.track('event', 'Mobile Menu', 'Link', link.title, menuState.level, true, () => {
      Turbolinks.visit(href)
    })
  }

  /**
   * Sets the menu state to display the given level and page id. Tracks page change event.
   *
   * @param e {object} click event.
   * @param level {number} level the page changing to.
   * @param id {string} id of page changing to.
   * @param name {string} display name of page/link clicked.
   */
  const onPageChange = (e, level, id, name) => {
    e.preventDefault()
    changeLevel(level, id)
    $.track('event', 'Mobile Menu', 'Page Change', name, menuState.level)

    return false
  }

  /**
   * Returns true if the page on the given level is currently set to visible.
   */
  const checkFocusability = (level, pageId) => {
    return menuState.level === level && menuState.visiblePages[level] === pageId
  }

  /**
   * returns an Icon if the title fits the switch / case
   *
   * @param {string} title - Sidebar title
   *
   * @returns {node | null} Either an Icon component or null
   */
  const getMenuIcon = title => {
    switch (title) {
      case 'Store Finder':
        return <MapMarkerIcon width="1.5rem" height="1.5rem" />
      case 'Email Offers':
        return <TagsIcon width="1.5rem" height="1.5rem" />
      case 'Contact Us':
        return <EnvelopeIcon width="1.5rem" height="1.5rem" />
      case 'My Orders':
        return <ShoppingCartIcon width="1.5rem" height="1.5rem" />
      case 'Sign in':
        return <UserProfileIcon width="1.5rem" height="1.5rem" />
      case 'Sign out':
        return <UserProfileIcon width="1.5rem" height="1.5rem" />
      case 'My Account':
        return <CogIcon width="1.5rem" height="1.5rem" />

      default:
        return null
    }
  }

  /**
   * Renders a link to a web site.
   */
  const renderLink = (item, idx) => {
    const isFocusable = checkFocusability(level, content.id)

    const ref = `mm-${content.uid}-${idx + 1}`

    const href = urlHelpers.addRef(item.link, ref)

    const isLogout = href.indexOf('/logout') > -1

    const linkIcon = getMenuIcon(item.title)

    return (
      <li
        key={idx}
        className={`is-clickable ${item.subtitle ? 'has-subtitle' : ''}`}
        role="menuitem"
      >
        <a
          href={href}
          data-method={isLogout ? 'delete' : undefined}
          tabIndex={isFocusable ? '0' : '-1'}
          onClick={e => onLinkClick(e, item, href)}
        >
          <div className={`${linkIcon ? 'has-icon' : ''}`}>
            {linkIcon && linkIcon}
            <div className="is-title">{item.title}</div>
          </div>
          {item.subtitle ? <div className="is-subtitle">{item.subtitle}</div> : false}
        </a>
      </li>
    )
  }

  /**
   * Renders a link to another mobile menu page. Also renders the linked mobile
   * menu page.
   */
  const renderPageLink = (item, idx) => {
    const isFocusable = checkFocusability(level, content.id)

    const childrenAreFocusable = checkFocusability(level + 1, item.link.id)

    const backButtonLabel = `Back to ${level ? previousTitle : 'Main Menu'}`

    // Only render items that are visible.
    item.link.items = item.link.items.filter(child => prismicHelpers.userHasPermission(child))

    // Don't render the link or child page if there will be nothing to show on
    // the next page.
    if (!item.link.items.length) {
      return false
    }

    return (
      <li
        key={idx}
        className={`is-clickable ${item.subtitle ? 'has-subtitle' : ''}`}
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={childrenAreFocusable}
      >
        <a
          href="#"
          onClick={e => onPageChange(e, level + 1, item.link.id, item.title)}
          tabIndex={isFocusable ? '0' : '-1'}
        >
          <div className="is-title">{item.title}</div>
          {item.subtitle ? <div className="is-subtitle">{item.subtitle}</div> : false}
          <ChevronIcon isRight className="is-right-icon" />
        </a>

        <MobileMenuPage
          content={item.link}
          changeLevel={changeLevel}
          level={level + 1}
          menuState={menuState}
          previousTitle={item.title}
        >
          <ul className="default-list">
            <li className={`${item.subtitle ? 'has-subtitle' : ''} text-center`} role="none">
              <span>
                <div className="is-title">{item.title}</div>
                {item.subtitle ? <div className="is-subtitle">{item.subtitle}</div> : false}
              </span>
            </li>
            <li className="is-clickable" role="menuitem">
              <a
                href="#"
                onClick={e => onPageChange(e, level, content.id, backButtonLabel)}
                tabIndex={childrenAreFocusable ? '0' : '-1'}
              >
                <ChevronIcon isLeft className="is-left-icon" />
                {backButtonLabel}
              </a>
            </li>
          </ul>
        </MobileMenuPage>
      </li>
    )
  }

  /**
   * Renders a divider.
   *
   * @param {string} idx - the array key
   *
   * @returns {function} react component
   */
  const renderDivider = idx => {
    return (
      <li key={idx} role="none">
        <hr />
      </li>
    )
  }

  /**
   * Renders a promo banner.
   */
  const renderBanner = (item, idx) => {
    item.promoPosition = `item_${idx + 1}`
    const data = {
      banners: [item],
      uid: item.promoId,
      promoName: item.promoName,
    }

    const isVisible = checkFocusability(level, content.id)

    return (
      <li key={idx} role="menuitem">
        <PromoBanners
          data={data}
          key={idx}
          overrideVisibleCheck
          isVisible={isVisible}
          type="simple"
        />
      </li>
    )
  }

  /**
   * Renders each item in the mobile menu page.
   */
  const renderItems = () => {
    return content.items.map((item, idx) => {
      switch (item.type) {
        case 'link':
          if (typeof item.link === 'object') {
            return renderPageLink(item, idx)
          }

          return renderLink(item, idx)

        case 'divider':
          return renderDivider(idx)
        case '4x1':
          return renderBanner(item, idx)
      }
    })
  }

  const pageIsVisible = menuState.visiblePages[level] === content.id

  return (
    <div
      className={`mobile-menu-page ${pageIsVisible ? 'is-visible' : 'is-hidden'}`}
      aria-hidden={!pageIsVisible}
      role="menu"
    >
      {children ? <header>{children}</header> : false}
      <div className="is-scrollable">
        <ul className="default-list">{renderItems()}</ul>
      </div>
    </div>
  )
}

MobileMenuPage.defaultProps = {
  level: 0,
  previousTitle: '',
}

MobileMenuPage.propTypes = {
  content: PropTypes.object.isRequired,
  changeLevel: PropTypes.func,
  level: PropTypes.number,
  previousTitle: PropTypes.string,
  toggleSidebar: PropTypes.func.isRequired,
  menuState: PropTypes.shape({
    level: PropTypes.number,
    visiblePages: PropTypes.arrayOf(PropTypes.string),
  }),
}

export default MobileMenuPage
